import { useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { Market } from '../dataProvider/types';
import { DsarRow } from './DsarRow';
import { useDsarQueue } from './useDsarQueue';
import type { DsarRow as Row } from './dsarQueueModel';

/**
 * The DSAR queue (D-dsar, the Phase 7 lead surface). A worklist over the first-class
 * dsarRequests collection: a DSAR is a member asking for everything the platform holds on them,
 * fulfilled by a compliance officer within a STATUTORY deadline. The deadline is DERIVED off
 * the injected clock (the ticket-SLA / suspension-elapse pattern), surfaced with compliance-
 * grade prominence: breached statutory deadlines sort to the top of the active worklist and are
 * unmissable. NO enforcement engine.
 *
 * Fulfilment assembles the member's OWN data into a viewable package (the DsarPackage view) and
 * records the request fulfilled through the chokepoint; rejection captures a mandatory reason.
 * All handling actions are audited for free.
 *
 * OVERSIGHT-GLOBAL with a region FACET: compliance is cross-region accountability, so the queue
 * spans markets by default (the audit-log posture, NOT the operational switcher). The region
 * facet narrows by the request's market by exact value. ROLE-gated to compliance and
 * super_admin (a capability read-gate would let a viewer's '*:read' wildcard in).
 */

const Section = ({
  title,
  rows,
  emptyText,
  testId,
  now,
}: {
  title: string;
  rows: Row[];
  emptyText: string;
  testId: string;
  now: number;
}) => (
  <Box sx={{ mb: 4 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title} ({rows.length})
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : (
      rows.map((row) => <DsarRow key={row.id} row={row} now={now} />)
    )}
  </Box>
);

/** The surface without the access wrapper. Exported so tests can inject `asOf` (the clock the
 *  statutory-deadline state is measured from). */
export const DsarQueueView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [regionFilter, setRegionFilter] = useState<Market | null>(null);
  const { view, isLoading } = useDsarQueue(now, regionFilter);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Compliance
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Data subject requests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Members asking for the data we hold on them, worked as a worklist
        against a statutory deadline. A breached deadline sorts to the top and
        is unmissable. Fulfilling a request assembles the member's own data for
        release; rejecting one needs a recorded reason. The view spans both
        markets by default.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="dsar-region-label">Region</InputLabel>
          <Select
            labelId="dsar-region-label"
            label="Region"
            value={regionFilter ?? 'all'}
            inputProps={{ 'data-testid': 'dsar-region' }}
            onChange={(e) =>
              setRegionFilter(
                e.target.value === 'all' ? null : (e.target.value as Market),
              )
            }
          >
            <MenuItem value="all">All markets</MenuItem>
            <MenuItem value="uk">UK</MenuItem>
            <MenuItem value="us">US</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isLoading || !view ? (
        <Loading />
      ) : (
        <>
          <Section
            title="Open requests"
            rows={view.active}
            emptyText="No open requests in scope."
            testId="dsar-section-active"
            now={now}
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Resolved"
            rows={view.resolved}
            emptyText="No resolved requests in scope."
            testId="dsar-section-resolved"
            now={now}
          />
        </>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /dsar. ROLE-gated to compliance and super_admin: the queue is a
 * compliance oversight surface, so a viewer's read-everywhere '*:read' wildcard must not reach
 * it and the operational roles have no business here (D-dsar).
 */
export const DsarQueue = () => (
  <RequireAccess roles={['compliance', 'super_admin']}>
    <DsarQueueView />
  </RequireAccess>
);
